/* /app/frontend/src/components/Seo.jsx
   Per-page SEO helper using direct DOM manipulation.

   Why not React 19 metadata hoisting? It only works reliably for
   <title>; <meta> and <link> tags get hoisted inconsistently when the
   render tree is deep or has other portals. DOM API in useEffect is
   100% predictable and only adds ~30 lines.

   Usage:
     <Seo
       title="Why The Resurrection Is Real History — 30-Day Course"
       description="30-day scholarly course on the historicity of the resurrection."
       canonical="https://faitharama.com/apologetics/history-resurrection"
       image="https://faitharama.com/apologetics/history-resurrection.png"
       type="article"
       schema={courseSchema}
     />
*/
import { useEffect } from "react";

const SITE = "Faitharama";
const DEFAULT_IMAGE = "https://faitharama.com/brand/faitharama-share-card.png";

/* Ensure a single <meta> tag with the given name/property/content.
   If one already exists, update its content. If not, create it.
   Returns the element so we can remove it on unmount if it was
   created BY US (not pre-existing from index.html). */
function upsertMeta(attr, value, content) {
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return { el, created };
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, created };
}

function upsertSchema(schemaObj, schemaId) {
  let el = document.head.querySelector(`script[data-schema-id="${schemaId}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-schema-id", schemaId);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schemaObj);
  return { el, created };
}

export default function Seo({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  schema = null,        // optional JSON-LD object
  keywords = null,      // optional comma-separated string
  schemaId = "page",    // unique id so multiple <Seo> instances don't collide
}) {
  const fullTitle = title
    ? `${title} | ${SITE}`
    : `${SITE} — Christian Wellness & Ministry, Made Simple`;

  useEffect(() => {
    // <title> — simple
    const prevTitle = document.title;
    document.title = fullTitle;

    // Tags we manage (tracked so we can clean up on unmount)
    const managed = [];
    const set = (r) => { if (r.created) managed.push(r.el); };

    if (description) set(upsertMeta("name", "description", description));
    if (keywords)    set(upsertMeta("name", "keywords",    keywords));
    if (canonical)   set(upsertLink("canonical", canonical));
    if (noindex)     set(upsertMeta("name", "robots", "noindex, follow"));

    set(upsertMeta("property", "og:type", type));
    set(upsertMeta("property", "og:title", fullTitle));
    if (description) set(upsertMeta("property", "og:description", description));
    if (canonical)   set(upsertMeta("property", "og:url", canonical));
    set(upsertMeta("property", "og:image", image));

    set(upsertMeta("name", "twitter:title", fullTitle));
    if (description) set(upsertMeta("name", "twitter:description", description));
    set(upsertMeta("name", "twitter:image", image));

    if (schema) set(upsertSchema(schema, schemaId));

    return () => {
      // Only remove tags we CREATED. Tags pre-existing from index.html
      // (e.g. the homepage default description) stay put.
      managed.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      document.title = prevTitle;
    };
  }, [fullTitle, description, canonical, image, type, noindex, keywords, schema, schemaId]);

  // No DOM render — everything happens in <head> via useEffect above.
  return null;
}
